import { useState, useEffect, useCallback } from "react";

export const useTournamentResults = (
  results = [],
  verificationStatus = null
) => {
  const [sidepotData] = useState([]);
  // Derived from verification — do not keep setter state; first paint must match lock status.
  const isPayoutFinalized = Boolean(verificationStatus?.payouts?.isVerified);
  const isResultsFinalized = Boolean(verificationStatus?.results?.isVerified);
  const [eventPagination, setEventPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("all");

  // Initialize pagination for each event
  useEffect(() => {
    if (results && results.length > 0) {
      const uniqueEvents = [...new Set(results.map((team) => team.eventId))];
      const initialPagination = {};

      uniqueEvents.forEach((eventId) => {
        const eventTeams = results.filter((team) => team.eventId === eventId);
        initialPagination[eventId] = {
          currentPage: 1,
          itemsPerPage: 10,
          totalItems: eventTeams.length,
          totalPages: Math.ceil(eventTeams.length / 10),
        };
      });

      setEventPagination(initialPagination);
    }
  }, [results]);

  // Pagination handlers
  const handlePageChange = useCallback((eventId, newPage) => {
    setEventPagination((prev) => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        currentPage: newPage,
      },
    }));
  }, []);

  const handleItemsPerPageChange = useCallback((eventId, newItemsPerPage) => {
    setEventPagination((prev) => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        currentPage: 1,
        itemsPerPage: newItemsPerPage,
        totalPages:
          newItemsPerPage === "All"
            ? 1
            : Math.ceil(prev[eventId].totalItems / newItemsPerPage),
      },
    }));
  }, []);

  // Get paginated data for an event
  const getPaginatedEventData = useCallback(
    (eventId, eventTeams) => {
      const pagination = eventPagination[eventId];
      if (!pagination) return eventTeams;

      if (pagination.itemsPerPage === "All") {
        return eventTeams;
      }

      const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
      const endIndex = startIndex + pagination.itemsPerPage;
      return eventTeams.slice(startIndex, endIndex);
    },
    [eventPagination]
  );

  // Filter results
  const getFilteredResults = useCallback(() => {
    let filteredResults = results;

    // Filter by event
    if (selectedEvent !== "all") {
      filteredResults = filteredResults.filter(
        (team) => team.eventId === selectedEvent
      );
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredResults = filteredResults.filter((team) => {
        // Search by team name
        const teamName = team.teamName?.toLowerCase() || "";
        if (teamName.includes(searchLower)) return true;

        // Search by bowler name (for individual events)
        const bowlerName = team.bowlerName?.toLowerCase() || "";
        if (bowlerName.includes(searchLower)) return true;

        // Search by bowler names within teams
        if (team.bowlers && Array.isArray(team.bowlers)) {
          const bowlerNames = team.bowlers
            .map((b) => b.name?.toLowerCase() || "")
            .join(" ");
          if (bowlerNames.includes(searchLower)) return true;
        }

        // Search by individual bowler names (alternative structure)
        if (team.individualBowlers && Array.isArray(team.individualBowlers)) {
          const individualBowlerNames = team.individualBowlers
            .map((b) => b.name?.toLowerCase() || "")
            .join(" ");
          if (individualBowlerNames.includes(searchLower)) return true;
        }

        return false;
      });
    }

    return filteredResults;
  }, [results, selectedEvent, searchTerm]);

  // Get unique events for filter dropdown
  const uniqueEvents = [...new Set(results.map((team) => team.eventId))];
  const eventNames = uniqueEvents.map((eventId) => {
    const eventTeam = results.find((team) => team.eventId === eventId);
    return { id: eventId, name: eventTeam?.eventName || "Unknown Event" };
  });

  // Get filtered results
  const filteredResults = getFilteredResults();

  return {
    // State
    sidepotData,
    isPayoutFinalized,
    isResultsFinalized,
    eventPagination,
    searchTerm,
    selectedEvent,
    filteredResults,
    eventNames,

    // Actions
    setSearchTerm,
    setSelectedEvent,
    handlePageChange,
    handleItemsPerPageChange,
    getPaginatedEventData,
  };
};
